import {
  Timestamp,
  doc,
  getDoc,
  getDocs,
  increment,
  limit as fbLimit,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  orderBy,
} from "firebase/firestore";
import { format } from "date-fns";
import { track } from "@/lib/analytics";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol, refDoc } from "./paths";
import {
  completionSchema,
  type Completion,
  type CompletionStatus,
  type Chore,
} from "@/lib/schemas";
import { todayMidnight } from "@/lib/utils/dates";
import { isChoreActiveToday } from "@/lib/utils/chores";
import { isXpOnlyKind } from "@/lib/utils/xp";
import {
  uploadProofPhoto,
  deleteProofPhoto,
} from "@/lib/storage/proofPhotos";

export type CreateCompletionOpts = {
  /**
   * Skompresowany blob zdjęcia-dowodu. Wymagany jeśli `chore.requiresPhoto`.
   * Upload trafia do `households/{hid}/completions/{cid}/proof.jpg`.
   */
  proofPhotoBlob?: Blob;
  /**
   * Ile dziecko zrobiło powtórzeń/minut. Wymagane gdy `chore.kind === "sport"`
   * i completer nie jest rodzicem (parent override pomija sport pola).
   */
  sportDone?: number;
};

export async function createCompletion(
  hid: string,
  uid: string,
  chore: Chore,
  opts: CreateCompletionOpts = {},
): Promise<string> {
  const db = getDb();
  const now = new Date();
  const today = Timestamp.fromDate(todayMidnight());

  if (chore.requiresPhoto && !opts.proofPhotoBlob) {
    throw new Error("To zadanie wymaga zdjęcia-dowodu");
  }

  // Schedule guard (HIGH-2): UI filtruje listę chore'ów po isChoreActiveToday,
  // ale klient z DevTools może wywołać createCompletion na dowolny chore
  // (np. weekly poniedziałek w sobotę → 2× nagroda). Powtarzamy tę samą
  // logikę po stronie db, bo Firestore rules nie potrafią porównać
  // schedule.daysOfWeek z dniem "dzisiaj".
  if (!isChoreActiveToday(chore, now)) {
    throw new Error("Tego zadania nie da się zgłosić dzisiaj");
  }

  // Duplicate guard: ten sam chore + ten sam user + ten sam dzień + status != rejected
  const dupQuery = query(
    refCol(db, paths.completions(hid)),
    where("choreId", "==", chore.id),
    where("completedBy", "==", uid),
    where("dueDate", "==", today),
    fbLimit(5),
  );
  const dupSnap = await getDocs(dupQuery);
  const dupDocs = dupSnap.docs.map((d) => d.data());
  const hasActive = dupDocs.some((d) => d.status !== "rejected");
  if (hasActive) {
    throw new Error("Zadanie już zgłoszone dzisiaj");
  }

  // Deterministic doc ID (HIGH-1): retry counter = liczba poprzednich
  // rejected na ten sam (chore, uid, dzień). Pierwszy submit = 0; po
  // każdym rejected counter rośnie, więc dziecko może spróbować ponownie
  // z innym zdjęciem — id się nie powtórzy.
  // Format separatora "_" — Firestore auto-id (base62) nie zawiera "_",
  // więc kombinacja jest jednoznacznie parsowalna.
  const dueDateISO = format(todayMidnight(), "yyyy-MM-dd");
  const counter = dupDocs.filter((d) => d.status === "rejected").length;
  const cid = `${chore.id}_${uid}_${dueDateISO}_${counter}`;
  const ref = refDoc(db, paths.completion(hid, cid));

  let proofPhotoUrl: string | undefined;
  let proofPhotoPath: string | undefined;
  if (opts.proofPhotoBlob) {
    const uploaded = await uploadProofPhoto(
      hid,
      ref.id,
      uid,
      opts.proofPhotoBlob,
    );
    proofPhotoUrl = uploaded.url;
    proofPhotoPath = uploaded.path;
  }

  // Snapshot funduszodawcy: chore.fundedBy lub fallback createdBy (autor chore).
  const fundedBy = chore.fundedBy ?? chore.createdBy;

  // Rola completera: rodzic NIGDY nie dostaje PLN — sam sobie nie placi.
  // Robi chore na rzecz rodziny dla XP (kind=family + rewardMinor=0).
  // Cala rodzina gra w expienie, tylko dziecko ma stronę finansową.
  // Rules tez egzekwują: dla parent allowed tylko {kind=family, reward=0}.
  const memberSnap = await getDoc(refDoc(db, paths.member(hid, uid)));
  const isParentCompleter = memberSnap.data()?.role === "parent";
  const effectiveKind = isParentCompleter ? "family" : chore.kind;
  const effectiveReward = isParentCompleter ? 0 : chore.rewardMinor;

  // Sport snapshot — tylko dla dziecka zgłaszającego sport chore.
  // Parent override (family) pomija pola sport (rules tez to wymuszają).
  const isSportSubmit =
    !isParentCompleter && chore.kind === "sport";
  if (isSportSubmit) {
    if (chore.sportUnit === undefined || chore.sportTarget === undefined) {
      throw new Error("Wyzwanie sportowe wymaga skonfigurowanego celu");
    }
    if (opts.sportDone === undefined || opts.sportDone < 0) {
      throw new Error("Wpisz ile zrobiłeś (liczba ≥ 0)");
    }
  }

  try {
    await setDoc(ref, {
      choreId: chore.id,
      choreTitle: chore.title,
      rewardMinor: effectiveReward,
      kind: effectiveKind,
      offline: chore.offline,
      size: chore.size,
      completedBy: uid,
      completedAt: serverTimestamp(),
      dueDate: today,
      status: "pending" satisfies CompletionStatus,
      fundedBy,
      ...(proofPhotoUrl ? { proofPhotoUrl } : {}),
      ...(proofPhotoPath ? { proofPhotoPath } : {}),
      ...(isSportSubmit
        ? {
            sportUnit: chore.sportUnit,
            sportTarget: chore.sportTarget,
            sportDone: opts.sportDone,
          }
        : {}),
    });
  } catch (e) {
    // Race protection (HIGH-1): Client SDK setDoc to upsert — nie ma
    // "create only" jak Admin SDK. Polegamy na asymetrii firestore.rules:
    //   - allow create: child OK (cross-ref pól z chore'em)
    //   - allow update: tylko parent
    // Druga karta która zapisze pod ten sam deterministic id widzi że
    // dokument istnieje → Firestore traktuje jako update → child dostaje
    // permission-denied. Ten sam kod pojawi się przy spoofingu pól z
    // DevTools (cross-ref blokuje create), co jest bonusem mylącym
    // atakującego. Dla uczciwego usera komunikat „już zgłoszone z innego
    // urządzenia" jest poprawny w obu scenariuszach.
    const code = (e as { code?: string })?.code;
    if (proofPhotoPath) {
      void deleteProofPhoto(proofPhotoPath);
    }
    if (code === "permission-denied") {
      throw new Error("Już zgłoszone z innego urządzenia. Odśwież appkę.");
    }
    throw e;
  }
  track("completion_submitted", {
    kind: chore.kind,
    offline: chore.offline,
    withPhoto: !!opts.proofPhotoBlob,
  });
  return ref.id;
}

export async function listCompletions(
  hid: string,
  opts: {
    status?: CompletionStatus;
    completedBy?: string;
    limitDays?: number;
  } = {},
): Promise<Completion[]> {
  const db = getDb();
  const constraints = [];
  if (opts.status) constraints.push(where("status", "==", opts.status));
  if (opts.completedBy)
    constraints.push(where("completedBy", "==", opts.completedBy));
  constraints.push(orderBy("completedAt", "desc"));
  const q = query(refCol(db, paths.completions(hid)), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.flatMap((d) => {
    const parsed = completionSchema.safeParse({ id: d.id, ...d.data() });
    return parsed.success ? [parsed.data] : [];
  });
}

/**
 * Atomowe zatwierdzenie:
 *   1. Sprawdź completion status === pending
 *   2. (sport) Pobierz chore (read-before-write) — żeby ewaluować auto-bump
 *   3. Zaktualizuj completion -> approved
 *   4. (sport + autoBump + >=7d od ostatniego) chore.sportTarget += bumpBy
 *   5. (extra/legacy) Utwórz transaction (earn) na rzecz dziecka
 *   6. (extra/legacy) Bump member.balanceMinor
 *
 * Dla `kind: "family"` i `kind: "sport"` pomijamy kroki 5-6 — pomoc rodzinie
 * i wyzwanie sportowe nie generują earn transaction ani nie zmieniają salda.
 * Dziecko dostaje tylko XP/odznaki.
 */
export async function approveCompletion(
  hid: string,
  completionId: string,
  approvedByUid: string,
): Promise<void> {
  const db = getDb();
  const completionRef = refDoc(db, paths.completion(hid, completionId));
  const txRef = doc(refCol(db, paths.transactions(hid)));

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(completionRef);
    if (!snap.exists()) throw new Error("Wykonanie nie istnieje");
    const data = snap.data();
    if (data.status !== "pending") {
      throw new Error("Wykonanie już rozpatrzone");
    }

    // Sport: pobierz chore w tym samym tx żeby ewaluować auto-bump.
    // Firestore wymaga reads PRZED writes — dlatego get() przed update().
    type ChoreSportFields = {
      sportAutoBumpBy?: number;
      sportTarget?: number;
      sportLastBumpAt?: { toMillis: () => number };
    };
    let choreRef: ReturnType<typeof refDoc> | null = null;
    let choreData: ChoreSportFields | null = null;
    if (data.kind === "sport" && typeof data.choreId === "string") {
      choreRef = refDoc(db, paths.chore(hid, data.choreId));
      const choreSnap = await tx.get(choreRef);
      if (choreSnap.exists()) {
        choreData = choreSnap.data() as ChoreSportFields;
      }
    }

    tx.update(completionRef, {
      status: "approved",
      approvedBy: approvedByUid,
      approvedAt: serverTimestamp(),
    });

    // Auto-progresja sport: jeśli `sportAutoBumpBy > 0` i minęło co najmniej
    // 7 dni od `sportLastBumpAt`, atomowo zwiększ chore.sportTarget i
    // odśwież sportLastBumpAt. Bezwarunkowe (niezależne od sportDone vs target)
    // — parent włącza/wyłącza checkboxem przy chore.
    if (choreRef && choreData) {
      const bumpBy = choreData.sportAutoBumpBy;
      const currentTarget = choreData.sportTarget;
      if (
        typeof bumpBy === "number" &&
        bumpBy > 0 &&
        typeof currentTarget === "number"
      ) {
        const lastMs = choreData.sportLastBumpAt?.toMillis() ?? 0;
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - lastMs >= sevenDaysMs) {
          tx.update(choreRef, {
            sportTarget: currentTarget + bumpBy,
            sportLastBumpAt: serverTimestamp(),
          });
        }
      }
    }

    // XP only (family, sport): brak transakcji i brak zmiany salda.
    // Brak pola `kind` (legacy) traktujemy jako "extra".
    if (isXpOnlyKind(data.kind ?? "extra")) return;

    const memberRef = refDoc(db, paths.member(hid, data.completedBy));

    tx.set(txRef, {
      userId: data.completedBy,
      amountMinor: data.rewardMinor,
      type: "earn",
      relatedCompletionId: completionId,
      ...(data.fundedBy ? { fundedBy: data.fundedBy } : {}),
      note: data.choreTitle,
      createdAt: serverTimestamp(),
      createdBy: approvedByUid,
    });

    tx.update(memberRef, {
      balanceMinor: increment(data.rewardMinor),
    });
  });
  track("completion_approved");
}

export async function rejectCompletion(
  hid: string,
  completionId: string,
  rejectedByUid: string,
  reason: string,
): Promise<void> {
  const db = getDb();
  const completionRef = refDoc(db, paths.completion(hid, completionId));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(completionRef);
    if (!snap.exists()) throw new Error("Wykonanie nie istnieje");
    if (snap.data().status !== "pending") {
      throw new Error("Wykonanie już rozpatrzone");
    }
    tx.update(completionRef, {
      status: "rejected",
      approvedBy: rejectedByUid,
      approvedAt: serverTimestamp(),
      rejectionReason: reason,
    });
  });
}
